import { useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react'
import { Camera, X } from 'lucide-react'
import { Button, Field, FormRow, Select, Textarea, Modal, Banner, useToast } from '../ds/index'
import { post, api } from '../lib/api'
import { Icon } from '../components/Icon'

type Kind = 'reply' | 'profile' | 'answers' | 'post' | 'other'
const KINDS: { value: Kind; label: string }[] = [
  { value: 'profile', label: 'Their profile' }, { value: 'post', label: 'The post' }, { value: 'reply', label: 'Your reply' },
  { value: 'answers', label: 'Their answers' }, { value: 'other', label: 'Something else' },
]
type Fields = Record<'name' | 'lives_in' | 'from' | 'phone' | 'email' | 'group' | 'posted_at' | 'post_text' | 'reply_text', string | null>
const LABELS: [keyof Fields, string][] = [
  ['name', 'Name'], ['lives_in', 'Lives in'], ['from', 'From'], ['phone', 'Phone they show'], ['email', 'Email they show'],
  ['group', 'Group'], ['posted_at', 'Posted'], ['post_text', 'Their post'], ['reply_text', 'Your reply'],
]
type Shot = { key: string; kind: Kind; dataUrl: string }
type Read = { id: string; fields: Fields; error: string | null; shots: { id: string; kind: string }[]; profile_url: string | null }
type Confirmed = { post_id: string; new_lead: boolean; zone: { status: string; zone?: string; business?: string | null; note?: string | null } | null }

const MAX_SIDE = 2000

/** Load a file as an image element. */
const load = (src: string) => new Promise<HTMLImageElement>((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => bad(new Error('That file is not an image we can read')); i.src = src })
const readFile = (f: File) => new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => bad(r.error); r.readAsDataURL(f) })

/** Cut a region (in the image's own pixels) out of an image, no larger than MAX_SIDE, as a JPEG. */
function cut(img: HTMLImageElement, box: { x: number; y: number; w: number; h: number }) {
  const scale = Math.min(1, MAX_SIDE / Math.max(box.w, box.h))
  const c = document.createElement('canvas')
  c.width = Math.max(1, Math.round(box.w * scale)); c.height = Math.max(1, Math.round(box.h * scale))
  c.getContext('2d')!.drawImage(img, box.x, box.y, box.w, box.h, 0, 0, c.width, c.height)
  return c.toDataURL('image/jpeg', 0.88)
}

/**
 * Crop a screenshot to the person (the package: other commenters' names and
 * faces are not kept). Drag a box over what to keep; only that part is sent.
 */
function Cropper({ src, onDone, onCancel }: { src: string; onDone: (dataUrl: string) => void; onCancel: () => void }) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [img, setImg] = useState<HTMLImageElement | null>(null)
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  useEffect(() => { load(src).then(setImg).catch(() => onCancel()) }, [src]) // eslint-disable-line react-hooks/exhaustive-deps
  const at = (e: RPointerEvent) => {
    const r = imgRef.current!.getBoundingClientRect()
    return { x: Math.min(Math.max(e.clientX - r.left, 0), r.width), y: Math.min(Math.max(e.clientY - r.top, 0), r.height) }
  }
  const down = (e: RPointerEvent) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); start.current = at(e); setBox({ ...start.current, w: 0, h: 0 }) }
  const move = (e: RPointerEvent) => {
    if (!start.current) return
    const p = at(e); const s = start.current
    setBox({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) })
  }
  const up = () => { start.current = null }
  const use = (whole: boolean) => {
    if (!img || !imgRef.current) return
    const shown = imgRef.current.getBoundingClientRect()
    const k = img.naturalWidth / shown.width
    const b = !whole && box && box.w > 12 && box.h > 12
      ? { x: box.x * k, y: box.y * k, w: box.w * k, h: box.h * k }
      : { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight }
    onDone(cut(img, b))
  }
  return (
    <Modal title="Crop to the person" onClose={onCancel}
      footer={<><Button variant="ghost" onClick={() => use(true)}>Keep it all</Button><Button onClick={() => use(false)} disabled={!box || box.w < 12}>Use this crop</Button></>}>
      <p style={{ marginTop: 0 }}>Drag a box around what to keep: the person, their words and your reply. Leave out other people's names and faces.</p>
      <div className="cropper" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <img ref={imgRef} src={src} alt="The screenshot to crop" draggable={false} />
        {box && box.w > 0 && <div className="cropper-box" style={{ left: box.x, top: box.y, width: box.w, height: box.h }} />}
      </div>
    </Modal>
  )
}

/**
 * The responder's screenshots, read into a lead (Ezra's package, section 1).
 * With an assignment: fills that lead. Without: a new lead from a post outside
 * the watched groups (item 6), in the trade and group the rep picks.
 */
export default function ScreenshotReader({ assignmentId, onDone }: { assignmentId?: string; onDone: (c: Confirmed) => void }) {
  const toast = useToast()
  const file = useRef<HTMLInputElement>(null)
  const [shots, setShots] = useState<Shot[]>([])
  const [queue, setQueue] = useState<string[]>([])
  const [link, setLink] = useState('')
  const [read, setRead] = useState<Read | null>(null)
  const [fields, setFields] = useState<Fields | null>(null)
  const [busy, setBusy] = useState(false)
  const [opts, setOpts] = useState<{ categories: { id: string; name: string }[]; groups: { id: string; name: string }[] } | null>(null)
  const [cat, setCat] = useState('')
  const [group, setGroup] = useState('')
  const [groupName, setGroupName] = useState('')
  useEffect(() => { if (!assignmentId) api<typeof opts>('/api/my/reads/options').then(setOpts).catch(() => setOpts({ categories: [], groups: [] })) }, [assignmentId])

  const pick = async (files: FileList | null) => {
    if (!files) return
    const room = 6 - shots.length - queue.length
    const list = [...files].filter((f) => /^image\//.test(f.type)).slice(0, Math.max(0, room))
    if (files.length > list.length) toast('Six screenshots at most, images only', 'neg')
    setQueue([...queue, ...(await Promise.all(list.map(readFile)))])
  }
  const cropped = (dataUrl: string) => {
    setShots([...shots, { key: `${Date.now()}-${shots.length}`, kind: shots.length === 0 ? 'profile' : 'post', dataUrl }])
    setQueue(queue.slice(1))
  }
  const send = async () => {
    setBusy(true)
    try {
      const r = await post<Read>('/api/my/reads', { assignment_id: assignmentId, profile_url: link || undefined, shots: shots.map((s) => ({ kind: s.kind, image: s.dataUrl })) })
      setRead(r); setFields(r.fields)
      if (!assignmentId && r.fields.group && !groupName) setGroupName(r.fields.group)
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const confirm = async () => {
    if (!read || !fields) return
    setBusy(true)
    try {
      const body: Record<string, unknown> = { fields }
      if (!assignmentId) { body.category_id = cat; if (group && group !== 'other') body.group_id = group; else body.group_name = groupName }
      const c = await post<Confirmed>(`/api/my/reads/${read.id}/confirm`, body)
      toast(c.new_lead ? 'Lead made. It shows in your leads in a moment, with the business to recommend.' : 'Lead filled in', 'pos')
      onDone(c)
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  if (read && fields) {
    return (
      <div className="stack">
        {read.error && <Banner tone="warn" title="The screenshots could not be read">{read.error}</Banner>}
        <div className="read-shots">{read.shots.map((s) => <img key={s.id} src={`/api/my/reads/${read.id}/shots/${s.id}`} alt={`Screenshot: ${s.kind}`} />)}</div>
        <p className="muted" style={{ margin: 0 }}>Check what was read. Fix anything wrong, and fill what it could not find.</p>
        <div className="form-grid">
          {LABELS.filter(([k]) => !k.endsWith('_text')).map(([k, label]) => (
            <FormRow key={k} label={label} htmlFor={`rf-${k}`} help={read.fields[k] ? undefined : 'Not found in the screenshots'}>
              <input id={`rf-${k}`} className="field" value={fields[k] || ''} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} />
            </FormRow>
          ))}
        </div>
        {LABELS.filter(([k]) => k.endsWith('_text')).map(([k, label]) => (
          <FormRow key={k} label={label} htmlFor={`rf-${k}`} help={read.fields[k] ? undefined : 'Not found in the screenshots'}>
            <Textarea id={`rf-${k}`} rows={3} value={fields[k] || ''} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} />
          </FormRow>
        ))}
        {!assignmentId && opts && (
          <div className="form-grid">
            <FormRow label="Trade" htmlFor="rf-cat">
              <Select id="rf-cat" value={cat} onChange={(e) => setCat(e.target.value)}>
                <option value="">Choose one</option>
                {opts.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormRow>
            <FormRow label="Group" htmlFor="rf-group">
              <Select id="rf-group" value={group} onChange={(e) => setGroup(e.target.value)}>
                <option value="">Choose one</option>
                {opts.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                <option value="other">A group not on my list</option>
              </Select>
            </FormRow>
            {(group === 'other' || !group) && <Field label="Group name" id="rf-gname" value={groupName} onChange={(e) => setGroupName(e.target.value)} />}
          </div>
        )}
        <div className="row-tight"><Button onClick={confirm} disabled={busy || (!assignmentId && !cat)}>{busy ? 'Saving' : 'Confirm'}</Button>
          <Button variant="ghost" onClick={() => { setRead(null); setFields(null) }}>Start over</Button></div>
      </div>
    )
  }

  return (
    <div className="stack">
      <p className="muted" style={{ margin: 0 }}>Add screenshots of their profile, the post, your reply and their answers. Crop each to the person; we read them and fill in the lead for you to check.</p>
      <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { void pick(e.target.files); e.target.value = '' }} />
      {shots.length > 0 && (
        <div className="shot-list">
          {shots.map((s, i) => (
            <div key={s.key} className="shot-item">
              <img src={s.dataUrl} alt={`Screenshot ${i + 1}`} />
              <Select aria-label={`What screenshot ${i + 1} shows`} value={s.kind} onChange={(e) => setShots(shots.map((x) => x.key === s.key ? { ...x, kind: e.target.value as Kind } : x))}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </Select>
              <button type="button" className="shot-remove" aria-label={`Remove screenshot ${i + 1}`} onClick={() => setShots(shots.filter((x) => x.key !== s.key))}><Icon of={X} size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <div><Button variant="secondary" onClick={() => file.current?.click()} disabled={shots.length >= 6}><Icon of={Camera} size={15} /> Add screenshots</Button></div>
      <Field label="Their profile link" id="rf-link" placeholder="https://www.facebook.com/..." value={link} onChange={(e) => setLink(e.target.value)} />
      <div><Button onClick={send} disabled={busy || !shots.length}>{busy ? 'Reading the screenshots' : 'Read them'}</Button></div>
      {queue[0] && <Cropper key={queue[0].slice(-40)} src={queue[0]} onDone={cropped} onCancel={() => setQueue(queue.slice(1))} />}
    </div>
  )
}
